import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import client from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import PlanPdfViewer from "@/components/PlanPdfViewer";
import { Ruler, ChevronLeft, Loader2, Send, Save, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const PRIORITY_META = {
  high: { label: "High priority", cls: "bg-error/10 text-error" },
  normal: { label: "Normal", cls: "bg-primary/10 text-primary" },
  low: { label: "Low", cls: "bg-slate-200/70 text-slate-600" },
};
const STATUS_META = {
  assigned: { label: "To do", cls: "bg-[#D97706]/12 text-[#D97706]" },
  submitted: { label: "Submitted", cls: "bg-[#0284C7]/12 text-[#0284C7]" },
  approved: { label: "Approved", cls: "bg-[#059669]/12 text-[#059669]" },
};
const TASK_META = { tiles: "Tiles", hybrid_flooring: "Hybrid flooring", other: "Other" };

function Badge({ meta }) {
  return <span className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold", meta?.cls)}>{meta?.label}</span>;
}

function Worksheet({ id, onBack }) {
  const qc = useQueryClient();
  const [results, setResults] = useState({});
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const q = useQuery({ queryKey: ["est-takeoff", id], queryFn: async () => (await client.get(`/takeoffs-queue/${id}`)).data });
  const t = q.data;

  useEffect(() => {
    if (t) {
      const byTask = Object.fromEntries((t.results || []).map((r) => [r.task_id, r]));
      const init = {};
      (t.tasks || []).forEach((tk) => {
        const r = byTask[tk.id] || {};
        init[tk.id] = { value: r.value ?? "", unit: r.unit || "m²", notes: r.notes || "" };
      });
      setResults(init);
      setNotes(t.estimator_notes || "");
    }
  }, [t?.id]);

  const save = async (submit) => {
    setBusy(true);
    try {
      const payload = {
        submit,
        estimator_notes: notes,
        results: (t.tasks || []).map((tk) => ({
          task_id: tk.id, area_label: tk.area_label,
          value: results[tk.id]?.value, unit: results[tk.id]?.unit || "m²", notes: results[tk.id]?.notes || "",
        })),
      };
      await client.put(`/takeoffs-queue/${id}/results`, payload);
      toast.success(submit ? "Takeoff submitted" : "Draft saved");
      qc.invalidateQueries({ queryKey: ["est-takeoff", id] });
      qc.invalidateQueries({ queryKey: ["est-queue"] });
      if (submit) onBack();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  if (q.isLoading) return <div className="h-64 animate-pulse rounded-2xl bg-muted/50" />;
  if (!t) return null;
  const done = t.status === "submitted" || t.status === "approved";

  return (
    <div className="animate-fade-up space-y-5">
      <button onClick={onBack} data-testid="est-back" className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-ink"><ChevronLeft className="h-4 w-4" />My queue</button>
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-accent px-2.5 py-0.5 font-mono text-xs font-bold text-accent-foreground">{t.ref}</span>
        <Badge meta={PRIORITY_META[t.priority]} /><Badge meta={STATUS_META[t.status]} />
      </div>

      {/* Plan viewer — HD, zoomable PDF */}
      <div className="rounded-2xl border border-slate-100 bg-white p-4">
        <h2 className="mb-2 font-display text-sm font-bold text-ink">Plan · {t.page_count} page{t.page_count === 1 ? "" : "s"} <span className="font-normal text-muted-foreground">— zoom in for measurements, or download for offline</span></h2>
        <PlanPdfViewer src={`/takeoffs/${id}/redacted-pdf`} filename={`${t.ref}-plan.pdf`} />
      </div>

      {/* Tasks + results */}
      <div className="rounded-2xl border border-slate-100 bg-white p-4">
        <h2 className="mb-3 font-display text-sm font-bold text-ink">Take-off worksheet</h2>
        <div className="space-y-3" data-testid="worksheet">
          {(t.tasks || []).map((tk) => (
            <div key={tk.id} className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
              <div className="flex items-center gap-2">
                <span className="rounded-md bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">{TASK_META[tk.type]}</span>
                <span className="font-medium text-ink">{tk.area_label || "—"}</span>
              </div>
              {tk.instructions && <p className="mt-1 text-xs text-muted-foreground">{tk.instructions}</p>}
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Input type="number" inputMode="decimal" disabled={done} value={results[tk.id]?.value ?? ""} data-testid={`result-value-${tk.id}`}
                  onChange={(e) => setResults((r) => ({ ...r, [tk.id]: { ...r[tk.id], value: e.target.value } }))} placeholder="Area" className="w-28 bg-white" />
                <Input disabled={done} value={results[tk.id]?.unit ?? "m²"} data-testid={`result-unit-${tk.id}`}
                  onChange={(e) => setResults((r) => ({ ...r, [tk.id]: { ...r[tk.id], unit: e.target.value } }))} className="w-20 bg-white" />
                <Input disabled={done} value={results[tk.id]?.notes ?? ""} data-testid={`result-notes-${tk.id}`}
                  onChange={(e) => setResults((r) => ({ ...r, [tk.id]: { ...r[tk.id], notes: e.target.value } }))} placeholder="Notes (optional)" className="min-w-[160px] flex-1 bg-white" />
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3">
          <Label className="mb-1.5 block text-xs">Overall notes</Label>
          <Textarea disabled={done} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="est-notes" placeholder="Anything the office should know" className="min-h-[60px]" />
        </div>
        {done ? (
          <div className="mt-3 flex items-center gap-1.5 rounded-lg bg-[#059669]/10 px-3 py-2 text-sm font-semibold text-[#059669]"><CheckCircle2 className="h-4 w-4" />Submitted — thanks!</div>
        ) : (
          <div className="mt-4 flex flex-wrap justify-end gap-2">
            <Button variant="outline" onClick={() => save(false)} disabled={busy} data-testid="save-draft-btn" className="gap-1.5"><Save className="h-4 w-4" />Save draft</Button>
            <Button onClick={() => save(true)} disabled={busy} data-testid="submit-results-btn" className="gap-1.5">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}Submit</Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function EstimatorTakeoffs() {
  const [openId, setOpenId] = useState(null);
  const q = useQuery({ queryKey: ["est-queue"], queryFn: async () => (await client.get("/takeoffs-queue/mine")).data });
  const items = q.data || [];

  if (openId) return <Worksheet id={openId} onBack={() => setOpenId(null)} />;

  return (
    <div className="animate-fade-up">
      <PageHeader title="My Takeoffs" subtitle="Plans assigned to you — measure the areas and submit" testid="est-header" />
      {q.isLoading ? (
        <div className="h-40 animate-pulse rounded-2xl bg-muted/50" />
      ) : items.length === 0 ? (
        <EmptyState icon={Ruler} title="Nothing assigned yet" hint="When the office assigns you a plan it'll appear here." />
      ) : (
        <div className="grid gap-3 md:grid-cols-2" data-testid="est-queue">
          {items.map((t) => (
            <button key={t.id} onClick={() => setOpenId(t.id)} data-testid={`est-card-${t.id}`}
              className="rounded-2xl border border-slate-100 bg-white p-4 text-left shadow-[0_2px_12px_rgba(35,52,90,0.04)] transition-all hover:border-primary/30 hover:shadow-[0_4px_18px_rgba(35,52,90,0.10)]">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-accent px-2.5 py-0.5 font-mono text-xs font-bold text-accent-foreground">{t.ref}</span>
                <Badge meta={PRIORITY_META[t.priority]} /><Badge meta={STATUS_META[t.status]} />
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(t.tasks || []).map((tk) => <span key={tk.id} className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-ink">{TASK_META[tk.type]}{tk.area_label ? `: ${tk.area_label}` : ""}</span>)}
              </div>
              <div className="mt-2 text-xs text-muted-foreground">{t.page_count} plan page{t.page_count === 1 ? "" : "s"}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
